import React from "react";
import { Link } from "react-router-dom";
import {
  UserCircle2, HeartPulse, BookOpen, FileText, Sparkles, Smartphone,
  Shield, ScrollText, Microscope, Stethoscope,
} from "lucide-react";
import LivingOrb from "@/components/me/LivingOrb";

/**
 * ME Living Stage — CTO Directive #81.
 * NOT a dashboard. NOT a hero panel. An *atmosphere*. The orb dominates
 * a quiet futuristic medical environment; capabilities orbit as soft
 * floating satellites, never wrapped in boxes.
 */
export default function MeCoreHero({ language = "tr" }) {
  const tr = language === "tr";

  // 6 capability satellites distributed evenly around the orb.
  // Position is purely polar — no card, no panel, just text + tiny icon
  // floating in the same room as ME.
  const caps = [
    { icon: UserCircle2, to: "/persons",    title: tr ? "Kişisel Asistan"    : "Personal Assistant",   ang: 200, r: 0.55 },
    { icon: HeartPulse,  to: "/medical",    title: tr ? "Sağlık Asistanı"    : "Health Assistant",     ang: 160, r: 0.62 },
    { icon: BookOpen,    to: "/knowledge",  title: tr ? "Bilimsel Ortak"     : "Scientific Partner",   ang: 240, r: 0.68 },
    { icon: FileText,    to: "/production", title: tr ? "Prodüksiyon Ortağı" : "Production Partner",   ang: 340, r: 0.55 },
    { icon: Sparkles,    to: "/lifestyle",  title: tr ? "Yaşam Koçu"         : "Life Coach",           ang: 20,  r: 0.62 },
    { icon: Smartphone,  to: "/devices",    title: tr ? "Cihaz Orkestratörü" : "Device Orchestrator",  ang: 60,  r: 0.66 },
  ];

  return (
    <section
      className="relative mb-8 overflow-hidden rounded-3xl"
      data-testid="me-core-hero"
      style={{
        // Quiet futuristic medical atmosphere — no flat background.
        background:
          "radial-gradient(900px 540px at 50% 35%, rgba(60,124,220,0.22), transparent 60%),"
          + "radial-gradient(700px 420px at 80% 100%, rgba(124,196,255,0.08), transparent 60%),"
          + "radial-gradient(700px 420px at 10% 0%, rgba(37,99,235,0.10), transparent 60%),"
          + "linear-gradient(180deg, rgba(5,11,24,0.92) 0%, rgba(2,5,20,0.96) 100%)",
        border: "1px solid rgba(124,196,255,0.10)",
        boxShadow: "0 1px 0 rgba(255,255,255,0.04) inset, 0 50px 120px -40px rgba(0,0,0,0.7)",
        minHeight: 640,
      }}
    >
      {/* Subtle scientific grid */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage:
            "linear-gradient(rgba(124,196,255,0.04) 1px, transparent 1px),"
            + "linear-gradient(90deg, rgba(124,196,255,0.04) 1px, transparent 1px)",
          backgroundSize: "64px 64px",
          maskImage: "radial-gradient(ellipse at 50% 50%, black 40%, transparent 80%)",
          WebkitMaskImage: "radial-gradient(ellipse at 50% 50%, black 40%, transparent 80%)",
        }}
      />

      {/* Light fog — soft volumetric layer */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(circle at 30% 18%, rgba(124,196,255,0.06), transparent 35%),"
            + "radial-gradient(circle at 70% 80%, rgba(124,196,255,0.04), transparent 40%)",
        }}
      />

      {/* Tagline — small, top, never dominating */}
      <div className="relative z-10 px-8 pt-8 text-center" data-testid="meds-banner">
        <div
          className="text-[10px] font-semibold uppercase tracking-[0.36em]"
          style={{ color: "#7cc4ff", textShadow: "0 0 18px rgba(124,196,255,0.5)" }}
        >
          ME · {tr ? "Buradayım" : "I am here"}
        </div>
      </div>

      {/* The Living Stage — orb absolutely centered + satellites in polar */}
      <div
        className="relative mx-auto"
        style={{ width: "min(900px, 100%)", height: 540 }}
        data-testid="living-stage"
      >
        {/* Orb (dominant element) */}
        <div
          className="absolute"
          style={{
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%)",
          }}
        >
          <LivingOrb state="idle" size={320} />
        </div>

        {/* Subtle orbital ring around the orb */}
        <div
          className="pointer-events-none absolute"
          style={{
            left: "50%",
            top: "50%",
            width: 480,
            height: 220,
            transform: "translate(-50%, -50%) rotate(-2deg)",
            borderRadius: "50%",
            border: "1px solid rgba(124,196,255,0.10)",
          }}
        />
        <div
          className="pointer-events-none absolute"
          style={{
            left: "50%",
            top: "50%",
            width: 620,
            height: 300,
            transform: "translate(-50%, -50%) rotate(3deg)",
            borderRadius: "50%",
            border: "1px solid rgba(124,196,255,0.06)",
          }}
        />

        {/* Capability satellites */}
        {caps.map((c) => {
          const a = (c.ang * Math.PI) / 180;
          // Elliptical orbit: x widens by 1.4 so satellites sit on the visible band.
          const radiusX = 360 * c.r;
          const radiusY = 200 * c.r;
          const x = Math.cos(a) * radiusX;
          const y = Math.sin(a) * radiusY;
          return (
            <Satellite
              key={c.to}
              icon={c.icon}
              to={c.to}
              title={c.title}
              x={x}
              y={y}
            />
          );
        })}
      </div>

      {/* Quiet identity strip — bottom */}
      <div
        className="relative z-10 flex flex-wrap items-center justify-center gap-2 px-6 pb-7 pt-2 text-[10px] uppercase tracking-[0.28em]"
        style={{ color: "#6b7da0" }}
        data-testid="safety-badges"
      >
        <Item icon={Shield}      label={tr ? "Güvenlik" : "Security"} />
        <Dot />
        <Item icon={ScrollText}  label="KVKK" />
        <Dot />
        <Item icon={UserCircle2} label={tr ? "Hasta Hakları" : "Patient Rights"} />
        <Dot />
        <Item icon={Microscope}  label={tr ? "Şeffaflık" : "Transparency"} />
        <Dot />
        <Item icon={Stethoscope} label={tr ? "Kanıt Temelli Tıp" : "Evidence-Based Medicine"} />
      </div>
    </section>
  );
}

function Satellite({ icon: Icon, to, title, x, y }) {
  return (
    <Link
      to={to}
      data-testid={`capability-${to.replace("/", "")}`}
      className="group absolute"
      style={{
        left: `calc(50% + ${x}px)`,
        top: `calc(50% + ${y}px)`,
        transform: "translate(-50%, -50%)",
      }}
    >
      <div className="flex flex-col items-center gap-2">
        <div
          className="flex h-9 w-9 items-center justify-center rounded-full transition-all duration-500 group-hover:scale-110"
          style={{
            background: "rgba(11,21,48,0.6)",
            border: "1px solid rgba(124,196,255,0.28)",
            color: "#a4d8ff",
            backdropFilter: "blur(12px)",
            boxShadow: "0 0 18px -4px rgba(124,196,255,0.5)",
          }}
        >
          <Icon className="h-4 w-4" strokeWidth={1.4} />
        </div>
        <div
          className="text-[11px] tracking-[0.04em] transition-colors duration-500 group-hover:text-white"
          style={{
            color: "#cfe1ff",
            textShadow: "0 0 14px rgba(124,196,255,0.45)",
          }}
        >
          {title}
        </div>
      </div>
    </Link>
  );
}

function Item({ icon: Icon, label }) {
  return (
    <span className="flex items-center gap-1.5">
      <Icon className="h-3 w-3" /> {label}
    </span>
  );
}
function Dot() {
  return <span style={{ color: "#2a3a5e" }}>·</span>;
}
